import { useEffect, useState } from 'react';
import { calculateLevel, calculateMatchXp } from '@big2/shared';
import { useI18n } from '../i18n.js';
import { getUserAvatar, getUserInitial } from '../utils/avatar.js';
import { ConfettiShower } from './ConfettiShower.js';
import { Icon } from './Icon.js';

export { ConfettiShower } from './ConfettiShower.js';

export const AVATAR_GRADIENTS = [
  'linear-gradient(135deg, #f472b6, #be185d)',
  'linear-gradient(135deg, #38bdf8, #1e3a8a)',
  'linear-gradient(135deg, #34d399, #065f46)',
  'linear-gradient(135deg, #fbbf24, #b45309)',
];

export interface SettlementRow {
  id: string;
  name: string;
  avatar: string;
  cards: number;
  twos: number;
  multiplier: number;
  penalty: number;
  isWinner?: boolean;
  isMe?: boolean;
  rank?: number;
  tricksWon?: number;
  folded?: boolean;
  scoreDelta?: number;
  totalScore?: number;
  xpGained?: number;
}

export interface WinCelebrationData {
  winnerName: string;
  didIWin: boolean;
  isTsaiRoom?: boolean;
  gameType?: 'big2' | 'muushig' | 'twentyone' | 'daaluu';
  rows: SettlementRow[];
  pot: number;
  isDemo?: boolean;
  matchOver?: boolean;
  matchWinnerName?: string;
  roundWinnerName?: string;
  bustedPlayerName?: string;
  bustedPlayerScore?: number;
  championScore?: number;
  pointLimit?: number | null;
  myEarnedXp?: number;
  myTotalXp?: number;
}

interface WinCelebrationProps {
  data: WinCelebrationData;
  canPlayAgain: boolean;
  onPlayAgain: (options?: { restartMatch?: boolean }) => void;
  onClose: () => void;
}

/**
 * Demo settlement data generator
 */
export function buildDemoWinData(
  customOpponents?: { id: string; name: string; cardCount: number }[],
): WinCelebrationData {
  const bots =
    customOpponents && customOpponents.length > 0
      ? customOpponents
      : [
          { id: 'bot1', name: 'Ava', cardCount: 13 },
          { id: 'bot2', name: 'Kenji', cardCount: 10 },
          { id: 'bot3', name: 'Mia', cardCount: 7 },
        ];

  const loserRows: SettlementRow[] = bots
    .map((b) => {
      const cards = b.cardCount > 0 ? b.cardCount : 13;
      const twos = Math.min(2, Math.floor(cards / 6));
      const multiplier = cards >= 13 ? 3 : cards >= 10 ? 2 : 1;
      const penalty = Math.round(cards * multiplier * Math.pow(2, twos));
      return {
        id: b.id,
        name: b.name,
        avatar: getUserAvatar(b.id, b.name).gradient,
        cards,
        twos,
        multiplier,
        penalty,
        isWinner: false,
        isMe: false,
      };
    })
    .sort((a, b) => a.cards - b.cards || a.penalty - b.penalty)
    .map((r, idx) => ({ ...r, rank: idx + 2 }));

  const pot = loserRows.reduce((sum, r) => sum + r.penalty, 0);

  const winnerRow: SettlementRow = {
    id: 'demo-you',
    name: 'You',
    avatar: getUserAvatar('demo-you', 'You').gradient,
    cards: 0,
    twos: 0,
    multiplier: 1,
    penalty: 0,
    isWinner: true,
    isMe: true,
    rank: 1,
  };

  return {
    winnerName: 'You',
    didIWin: true,
    rows: [winnerRow, ...loserRows],
    pot,
    isDemo: true,
  };
}

export function WinCelebration({ data, canPlayAgain, onPlayAgain, onClose }: WinCelebrationProps) {
  const { lang } = useI18n();
  const isMn = lang === 'mn';
  const [showSettlement, setShowSettlement] = useState(false);
  const [isStarting, setIsStarting] = useState(false);

  const isMuushig = data.gameType === 'muushig';
  const isDaaluu = data.gameType === 'daaluu';
  const isTwentyOne = data.gameType === 'twentyone' || isDaaluu;
  const isBig2 = data.gameType === 'big2' || !data.gameType;
  const isMatchOver = Boolean(data.matchOver);

  useEffect(() => {
    const delay = isMuushig ? 2600 : 600;
    const timer = setTimeout(() => {
      setShowSettlement(true);
    }, delay);
    return () => clearTimeout(timer);
  }, [isMuushig]);

  useEffect(() => {
    setIsStarting(false);
  }, [data]);

  const championName = data.matchWinnerName || data.winnerName;
  const roundWinnerName = data.roundWinnerName;
  const championRow = data.rows.find((r) => r.isWinner || r.rank === 1) || data.rows[0];
  const championScore = data.championScore ?? championRow?.totalScore ?? 0;

  // Daaluu ends with the winner holding all the tea, so the table of zeros adds nothing: just name the winner.
  const daaluuLabel = data.didIWin ? (isMn ? 'Та хожлоо' : 'You won') : (isMn ? 'Ялагч' : 'Winner');

  // Header copy
  const heroTitle = isDaaluu
    ? `${daaluuLabel}: ${data.winnerName}`
    : isMatchOver
    ? data.didIWin
      ? isMn ? '🏆 ТЭМЦЭЭНИЙ АВАРГА!' : '🏆 MATCH CHAMPION!'
      : isMn ? `👑 ${championName} АВАРГА БОЛЛОО` : `👑 ${championName} WON THE MATCH`
    : data.didIWin
    ? isMn ? '🎉 ТА ХОЖЛОО!' : '🎉 YOU WON!'
    : isMn ? `👑 ${data.winnerName} ХОЖЛОО` : `👑 ${data.winnerName} WON`;

  const heroSubtitle = isMatchOver
    ? isMn
      ? `${championName} нийт ${championScore} торгуулийн оноогоор тэргүүлж (${data.pointLimit || 30} онооны хязгаар) аваргын цом хүртлээ`
      : `${championName} claimed match victory with the lowest penalty score of ${championScore} pts (${data.pointLimit || 30} pt limit)`
    : isMuushig
    ? isMatchOver
      ? data.didIWin
        ? isMn ? 'Та 0 оноонд хүрч түрүүллээ!' : 'You reached 0 and won the match!'
        : isMn ? `${championName} 0 оноонд хүрч хожлоо` : `${championName} reached 0 and won`
      : isMn
      ? `${data.winnerName} хамгийн олон гэр авч энэ өрөгт түрүүллээ`
      : `${data.winnerName} won this round with the most tricks`
    : isMn
    ? 'Бүх модоо хамгийн түрүүнд дуусгаж өргийг хожлоо'
    : 'First player to clear their entire hand';

  return (
    <>
      {showSettlement && <ConfettiShower />}

      {showSettlement && (
        <div
          className="win-modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label={heroTitle}
        >
          <div className={`win-modal-card${isMatchOver ? ' is-match-over' : ''}`}>
            {/* 1. Prestigious Hero Header */}
            <div className={`win-hero-header${isMatchOver ? ' is-match-over' : ''}`}>
              {isDaaluu ? null : isMatchOver ? (
                <div className="win-match-banner-tag">
                  🏆 {data.pointLimit || 30}-{isMn ? 'ОНООНЫ ТЭМЦЭЭН ӨНДӨРЛӨВ' : 'POINT MATCH FINALE'}
                </div>
              ) : (
                <div className="win-round-banner-tag">
                  {isMn ? 'ҮЕИЙН ДҮН' : 'ROUND SETTLEMENT'}
                </div>
              )}
              <div className={`win-hero-crest${isMatchOver ? ' is-match-trophy' : ''}`} aria-hidden="true">
                <span className={isMatchOver ? 'win-hero-trophy' : 'win-hero-crown'}>
                  {isMatchOver ? '🏆' : '👑'}
                </span>
              </div>
              {isDaaluu ? (
                <>
                  <p className="win-daaluu-label">{daaluuLabel}</p>
                  <h2 className="win-hero-title win-daaluu-winner-name">{data.winnerName}</h2>
                </>
              ) : (
                <>
                  <h2 className="win-hero-title">{heroTitle}</h2>
                  <p className="win-hero-subtitle">{heroSubtitle}</p>
                </>
              )}
            </div>

            {/* 1.5 Match Champion Spotlight & Reason Banner */}
            {isMatchOver && isBig2 && (
              <>
                <div className="win-champion-spotlight">
                  <div className="win-champion-left">
                    <div
                      className="win-champion-avatar"
                      style={{ background: championRow?.avatar || getUserAvatar(championRow?.id || 'champ', championName).gradient }}
                      aria-hidden="true"
                    >
                      👑
                    </div>
                    <div className="win-champion-info">
                      <span className="win-champion-tag">🥇 {isMn ? 'Тэмцээний Аварга' : 'Match Champion'}</span>
                      <span className="win-champion-name">{championName}</span>
                    </div>
                  </div>
                  <div className="win-champion-score-box">
                    <span className="win-champion-score-num">{championScore} pt</span>
                    <span className="win-champion-score-lbl">{isMn ? 'Торгуулийн оноо' : 'Penalty Score'}</span>
                  </div>
                </div>

                {(data.bustedPlayerName || (roundWinnerName && roundWinnerName !== championName)) && (
                  <div className="win-match-reason-box">
                    {data.bustedPlayerName && (
                      <div className="win-match-reason-item is-busted">
                        <span>💥</span>
                        <span>
                          {isMn
                            ? `${data.bustedPlayerName} ${data.bustedPlayerScore ?? ''} оноонд хүрснээр (${data.pointLimit || 30} онооны хязгаар) тэмцээн өндөрлөлөө`
                            : `Match concluded: ${data.bustedPlayerName} reached ${data.bustedPlayerScore ?? ''} pts (${data.pointLimit || 30} pt limit)`}
                        </span>
                      </div>
                    )}
                    {roundWinnerName && roundWinnerName !== championName && (
                      <div className="win-match-reason-item is-round-diff">
                        <span>⚡</span>
                        <span>
                          {isMn
                            ? `Сүүлийн өргийг ${roundWinnerName} дуусгасан ч, нийт оноогоор ${championName} аваргын цом хүртлээ`
                            : `${roundWinnerName} cleared cards in the final round, but ${championName} won the match on total points`}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {/* 1.8 XP Rewards & Level Progression */}
            {(() => {
              const myRow = data.rows.find((r) => r.isMe);
              const earnedXp = data.myEarnedXp ?? myRow?.xpGained ?? calculateMatchXp(data.didIWin, Boolean(data.isTsaiRoom));
              const currentTotalXp = data.myTotalXp !== undefined ? data.myTotalXp : earnedXp;
              const levelInfo = calculateLevel(currentTotalXp, isMn ? 'mn' : 'en');
              return (
                <div className="win-xp-reward-card">
                  <div className="win-xp-reward-header">
                    <div className="win-xp-reward-badge">
                      <span className="win-xp-lightning" aria-hidden="true">⚡</span>
                      <span className="win-xp-amount">+{earnedXp} XP</span>
                      <span className="win-xp-tag">
                        {data.didIWin
                          ? (isMn ? 'Ялалтын шагнал' : 'Victory Bonus')
                          : (isMn ? 'Тоглолтын XP' : 'Match XP')}
                      </span>
                    </div>
                    <div className="win-xp-level-badge">
                      <span className="win-xp-rank-icon">{levelInfo.rankBadge}</span>
                      <span className="win-xp-level-text">Lv. {levelInfo.level}</span>
                      <span className="win-xp-rank-title">· {levelInfo.rankTitle}</span>
                    </div>
                  </div>
                  <div
                    className="win-xp-progress-bar-bg"
                    title={`${levelInfo.currentLevelXp} / ${levelInfo.nextLevelXp} XP (${levelInfo.progressPercent}%)`}
                  >
                    <div
                      className="win-xp-progress-bar-fill"
                      style={{ width: `${Math.max(5, levelInfo.progressPercent)}%` }}
                    />
                  </div>
                  <div className="win-xp-progress-labels">
                    <span>
                      {isMn ? 'Дараагийн түвшин хүртэл' : 'Next level'}:{' '}
                      <strong>{levelInfo.currentLevelXp} / {levelInfo.nextLevelXp} XP</strong>
                    </span>
                    <span className="win-xp-percent">{levelInfo.progressPercent}%</span>
                  </div>
                </div>
              );
            })()}

            {/* 2. Standings & Leaderboard Table (Daaluu shows only the winner) */}
            {!isDaaluu && <div className={`win-standings-container${isMatchOver ? ' is-match-over' : ''}`}>
              {isMatchOver && isBig2 && (
                <div className="win-standings-header">
                  <span>🏁 {isMn ? 'Тэмцээний эцсийн дүн' : 'Final Match Standings'}</span>
                  <span>{isMn ? 'Нийт торгууль' : 'Total Score'}</span>
                </div>
              )}
              <div className="win-standings-list">
                {data.rows.map((row) => {
                  const isWinner = Boolean(row.isWinner);
                  const isMe = Boolean(row.isMe);
                  const rank = row.rank ?? 1;
                  const isBusted = isMatchOver && isBig2 && (row.totalScore ?? 0) >= (data.pointLimit || 30);

                  // Hand Status description (Clean, no dense math walls)
                  let handSummary = '';
                  let multiplierBadge = '';

                  if (isMatchOver && isBig2) {
                    if (row.cards === 0 || (roundWinnerName && row.name === roundWinnerName)) {
                      handSummary = isMn ? 'Сүүлийн өрөгт: 0 мод (Өргийн ялагч)' : 'Final round: 0 cards (Round winner)';
                    } else {
                      handSummary = isMn ? `Сүүлийн өрөгт: ${row.cards} мод` : `Final round: ${row.cards} cards left`;
                      if (row.multiplier > 1) {
                        multiplierBadge = `${row.multiplier}×`;
                      }
                    }
                  } else if (isMuushig) {
                    handSummary = isMatchOver && isWinner
                      ? isMn ? '0 оноонд хүрч түрүүллээ' : 'Reached 0 points'
                      : row.folded
                      ? isMn ? 'Өнжсөн (0)' : 'Sat out (0)'
                      : (row.tricksWon ?? 0) === 0
                      ? isMn ? '0 гэр (Муушиг торгууль)' : '0 tricks (Muushig penalty)'
                      : isMn ? `${row.tricksWon ?? 0} гэр авсан` : `${row.tricksWon ?? 0} tricks`;
                  } else if (isWinner) {
                    handSummary = isMn ? '0 мод (Ялагч)' : '0 cards (Winner)';
                  } else if (isTwentyOne) {
                    handSummary = isMn ? 'Бэлэн' : 'Showdown';
                  } else {
                    handSummary = isMn ? `${row.cards} мод үлдсэн` : `${row.cards} cards left`;
                    if (row.multiplier > 1) {
                      multiplierBadge = `${row.multiplier}×`;
                    }
                  }

                  // Score / Tea Delta display
                  let scoreValueText = '';
                  let scoreSubText = '';
                  let isScorePositive = isWinner;

                  if (isMatchOver && isBig2) {
                    const total = row.totalScore ?? 0;
                    scoreValueText = `${total} pt`;
                    if (isBusted) {
                      scoreSubText = isMn ? '💥 Хязгаарт хүрсэн' : '💥 Busted / Limit reached';
                      isScorePositive = false;
                    } else if (rank === 1) {
                      scoreSubText = isMn ? '🏆 Аварга' : '🏆 Champion';
                      isScorePositive = true;
                    } else {
                      scoreSubText = isMn ? `${rank}-р байр` : `Rank #${rank}`;
                      isScorePositive = false;
                    }
                  } else if (data.isTsaiRoom) {
                    if (isWinner) {
                      scoreValueText = `+${data.pot} 🍵`;
                      isScorePositive = true;
                    } else {
                      const delta = row.scoreDelta ?? 0;
                      scoreValueText = delta !== 0 ? `${delta} 🍵` : `-${row.penalty} 🍵`;
                      isScorePositive = false;
                    }
                  } else if (isMuushig) {
                    const total = row.totalScore ?? 15;
                    const delta = row.scoreDelta ?? 0;
                    scoreValueText = isMn ? `${total} оноо` : `${total} pts`;
                    scoreSubText = delta > 0
                      ? isMn ? '+5 МУУШИГ!' : '+5 MUUSHIG!'
                      : delta < 0
                      ? isMn ? `${delta} оноо` : `${delta} pts`
                      : isMn ? 'Өнжсөн (0)' : 'Sat out (0)';
                    isScorePositive = delta < 0 || isWinner;
                  } else if (isTwentyOne) {
                    const delta = row.scoreDelta ?? 0;
                    scoreValueText = delta > 0 ? `+${delta}` : `${delta}`;
                    isScorePositive = delta > 0;
                  } else {
                    // Big Two round
                    if (isWinner) {
                      scoreValueText = '+0';
                      isScorePositive = true;
                    } else {
                      scoreValueText = `+${row.penalty}`;
                      isScorePositive = false;
                    }
                    if (row.totalScore !== undefined && data.pointLimit) {
                      scoreSubText = isMn ? `Нийт: ${row.totalScore}/${data.pointLimit}` : `Total: ${row.totalScore}/${data.pointLimit}`;
                    }
                  }

                  const rankEmoji = rank === 1 ? '🥇' : rank === 2 ? '🥈' : rank === 3 ? '🥉' : `#${rank}`;

                  return (
                    <div
                      key={row.id}
                      className={`win-player-row${isWinner ? ' is-winner' : ''}${isMe ? ' is-me' : ''}${isBusted ? ' is-busted-player' : ''} rank-${rank}`}
                    >
                      <div className="win-player-left">
                        <span className={`win-rank-badge rank-${rank}`} title={`Rank #${rank}`}>
                          {rankEmoji}
                        </span>

                        <div
                          className="win-player-avatar"
                          style={{ background: row.avatar || getUserAvatar(row.id, row.name).gradient }}
                          aria-hidden="true"
                        >
                          {isWinner ? '👑' : getUserInitial(row.name)}
                        </div>

                        <div className="win-player-info">
                          <div className="win-name-row">
                            <span className="win-player-name">{row.name}</span>
                            {isMe && <span className="win-me-badge">{isMn ? 'Та' : 'You'}</span>}
                          </div>
                          <div className="win-hand-desc">
                            <span>{handSummary}</span>
                            {multiplierBadge && (
                              <span className="win-mult-tag">{multiplierBadge}</span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="win-player-right">
                        <div className={`win-score-primary ${isScorePositive ? 'is-pos' : 'is-neg'}`}>
                          {scoreValueText}
                        </div>
                        {scoreSubText && (
                          <div className="win-score-sub">{scoreSubText}</div>
                        )}
                        {row.xpGained !== undefined && row.xpGained > 0 && (
                          <div className="win-row-xp-gain" title={isMn ? 'Авсан туршлага' : 'XP earned'}>
                            ⚡ +{row.xpGained} XP
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>}

            {/* 3. Action Buttons */}
            <div className="win-actions">
              <button
                type="button"
                className={`win-btn-primary${isMatchOver ? ' is-match-over-cta' : ''}`}
                onClick={() => {
                  if (isStarting) return;
                  if (canPlayAgain) {
                    setIsStarting(true);
                    setTimeout(() => setIsStarting(false), 6000);
                    onPlayAgain?.({ restartMatch: Boolean(data.matchOver) });
                  } else {
                    onClose();
                  }
                }}
                disabled={isStarting}
              >
                <Icon name="rotate-ccw" size={17} />
                <span>
                  {isStarting
                    ? isMn ? 'Эхлүүлж байна...' : 'Starting...'
                    : !canPlayAgain
                    ? isMn ? 'Өрөө рүү буцах' : 'Back to Lobby'
                    : isMatchOver
                    ? isMn ? '🏆 Шинэ тэмцээн эхлүүлэх' : '🏆 Start New Match'
                    : (data.gameType === 'big2' || data.gameType === 'muushig') && !data.matchOver
                    ? isMn ? 'Дараагийн үе эхлүүлэх' : 'Play Next Round'
                    : isMn ? 'Дахин тоглох' : 'Play Again'}
                </span>
              </button>

              {!data.isDemo && canPlayAgain && (data.gameType === 'big2' || data.gameType === 'muushig') && !data.matchOver && (Boolean(data.pointLimit) || data.rows.some((r) => (r.totalScore ?? 0) > 0)) && (
                <button
                  type="button"
                  className="win-btn-secondary"
                  onClick={() => {
                    if (isStarting) return;
                    setIsStarting(true);
                    setTimeout(() => setIsStarting(false), 6000);
                    onPlayAgain?.({ restartMatch: true });
                  }}
                  disabled={isStarting}
                >
                  <span>{isMn ? '🔄 Оноо тэглэж, шинээр эхлэх' : '🔄 Restart Match (Reset Points)'}</span>
                </button>
              )}

              {!data.isDemo && canPlayAgain && (
                <button
                  type="button"
                  className="win-btn-ghost"
                  onClick={onClose}
                >
                  <span>{isMn ? 'Өрөө рүү буцах' : 'Back to Lobby'}</span>
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
